/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { SearchBox, Link, FocusZone, FocusZoneDirection, Shimmer, ShimmerElementType, TooltipHost, TooltipOverflowMode } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';

const getStyles = (_component, { palette = {} }) => ({
  container: {
    border: `1px solid ${palette.neutralSecondaryAlt}`,
    width: '300px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  listBox: {
    flex: '1 1 auto',
    overflow: 'auto',
    padding: '0 4px',
  },
  searchBox: {
    flex: '0 0 auto',
    height: '36px',
  },
  item: {
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.neutralLighter,
    },
    padding: '0px 4px',
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1 1 0px',
    marginRight: '4px',
  },
  bulkControl: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
  },
  notSelected: {
    color: palette.themePrimary,
  },
});

const List = ({
  items,
  onItemSelect,
  onItemDeselect,
  isSelectedList,
  classes,
  onSearch,
  isFiltered,
  onBulkOperation,
  defaultItemCount,
  isSearchBoxEnabled,
  isSearchBoxAutoTrigger,
  customizedActionText,
  isActionAlwaysEnabled,
  isActionAlwaysDisabled,
  isPaginationEnabled,
  customRenderBulkControl,
  customRenderItems,
  customRenderSelectedItems,
  customSearchPlaceHolder,
}) => {
  const i18n = useContext(I18nContext);
  const searchPlaceholder = useMemo(
    () => (
      customSearchPlaceHolder || (isSelectedList ? i18n.getString(_TL_('Search selected items')) : i18n.getString(_TL_('Search available items')))),
    [customSearchPlaceHolder, isSelectedList, i18n]
  );

  const toggleSelection = useCallback((item) => {
    if (item.isSelected) {
      onItemDeselect(item);
    } else {
      onItemSelect(item);
    }
  }, [onItemSelect, onItemDeselect]);

  const getItemName = useCallback(itemName => (
    <TooltipHost
      hostClassName={classes.name}
      overflowMode={TooltipOverflowMode.Self}
      content={itemName}
    >
      {itemName}
    </TooltipHost>
  ), [classes.name]);

  const renderItems = useCallback(
    () => {
      if (customRenderItems) {
        return customRenderItems(items, toggleSelection, classes);
      }
      return (
        <div role="listbox" className={classes.listBox} aria-label={i18n.getString(_TL_('Available items'))}>
          {items && items.map(item => (
            // keydown event is handled by FocusZone
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              className={classNames(classes.item, 'item', `item-${item.id}`)}
              tabIndex={0}
              role="option"
              data-is-focusable
              aria-label={item.name}
              aria-selected={item.isSelected}
              key={item.id}
              onClick={() => (item.isEditDisabled ? _.noop : toggleSelection(item))}
            >
              {getItemName(item.name)}
              <div className={!item.isSelected ? classes.notSelected : ''}>
                { item.isSelected ? i18n.getString(_TL_('added')) : i18n.getString(_TL_('add')) }
              </div>
            </div>))}
        </div>);
    },
    [classes, customRenderItems, getItemName, i18n, items, toggleSelection]
  );

  const renderShimmer = useCallback(
    () => (
      <div role="listbox" className={classes.listBox} aria-label={i18n.getString(_TL_('Shimmer'))}>
        {_.range(defaultItemCount).map(index => (<Shimmer
          key={index}
          className={classes.item}
          shimmerElements={[{ type: ShimmerElementType.line, height: 30 }]}
        />))}
      </div>
    )
    , [classes.item, classes.listBox, defaultItemCount, i18n]
  );

  const renderSelectedItems = useCallback(
    () => {
      if (customRenderSelectedItems) {
        return customRenderSelectedItems(items, onItemDeselect, classes);
      }
      return (
        <div role="listbox" className={classes.listBox} aria-label={i18n.getString(_TL_('Selected items'))}>
          {items && items.map(item => (
            // keydown event is handled by FocusZone
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              className={classNames(classes.item, 'item')}
              tabIndex={0}
              role="option"
              aria-selected
              data-is-focusable
              aria-label={item.name}
              key={item.id}
              onClick={() => (item.isEditDisabled ? _.noop : onItemDeselect(item))}
            >
              {getItemName(item.name)}
              {!item.isEditDisabled && <div>{ i18n.getString(_TL_('Remove')) } </div>}
            </div>))}
        </div>);
    },
    [classes, customRenderSelectedItems, getItemName, i18n, items, onItemDeselect]
  );

  const renderBulkControl = useCallback(() => {
    const length = items ? items.length : 0;

    const customerRendered = customRenderBulkControl && customRenderBulkControl(items, onBulkOperation, isSelectedList, classes);
    if (customerRendered) {
      return customerRendered;
    }

    let text;
    let actionText;
    let disabled = false;
    if (isSelectedList) {
      text = isFiltered ?
        curlyTemplate(i18n.getString(_TL_('Filtered items: {{count}}')))({ count: length }) :
        curlyTemplate(i18n.getString(_TL_('Selected items: {{count}}')))({ count: length });
      actionText = customizedActionText || (isFiltered ? i18n.getString(_TL_('Remove all filtered')) : i18n.getString(_TL_('Remove all')));
      disabled = isActionAlwaysDisabled || (!isActionAlwaysEnabled && length === 0);
    } else {
      text = isFiltered ?
        curlyTemplate(i18n.getString(_TL_('Filtered items: {{count}}')))({ count: length }) :
        curlyTemplate(i18n.getString(_TL_('Available items: {{count}}')))({ count: length });
      actionText = customizedActionText || (isPaginationEnabled ? i18n.getString(_TL_('Add all on this page')) : i18n.getString(_TL_('Add all')));
      disabled = isActionAlwaysDisabled || (!isActionAlwaysEnabled && _.every(items, { isSelected: true }));
    }

    return (
      <div className={classNames(classes.bulkControl, 'bulk-control')} >
        <div className="description">
          <Shimmer
            isDataLoaded={items !== null}
            shimmerElements={[{ type: ShimmerElementType.line, height: 20, width: 120 }]}
          >
            {text}
          </Shimmer>
        </div>
        <Link onClick={onBulkOperation} disabled={disabled}>{actionText}</Link>
      </div>
    );
  }, [items, customRenderBulkControl, isSelectedList, classes, onBulkOperation, isFiltered, i18n, customizedActionText, isActionAlwaysDisabled, isActionAlwaysEnabled, isPaginationEnabled]);

  return (
    <FocusZone direction={FocusZoneDirection.vertical}>
      <div className={classNames(isSelectedList ? 'selected-items' : 'items', classes.container)}>
        {(isSearchBoxEnabled && ((items && items.length > 0) || isFiltered || !isSelectedList)) && <SearchBox
          className={classNames('search-box', classes.searchBox)}
          placeholder={searchPlaceholder}
          onSearch={onSearch}
          onChange={(event, value) => (isSearchBoxAutoTrigger ? onSearch(value) : _.noop())}
          onClear={() => onSearch('')}
          underlined
        />}
        {renderBulkControl()}
        { items === null && renderShimmer() }
        {isSelectedList ? renderSelectedItems() : renderItems()}
      </div>
    </FocusZone>
  );
};

List.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string,
    listBox: PropTypes.string,
    searchBox: PropTypes.string,
    item: PropTypes.string,
    bulkControl: PropTypes.string,
    notSelected: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  customRenderBulkControl: PropTypes.func,
  customRenderItems: PropTypes.func,
  customRenderSelectedItems: PropTypes.func,
  customSearchPlaceHolder: PropTypes.string,
  onItemSelect: PropTypes.func,
  onItemDeselect: PropTypes.func,
  isSelectedList: PropTypes.bool,
  onSearch: PropTypes.func,
  onBulkOperation: PropTypes.func,
  isFiltered: PropTypes.bool,
  defaultItemCount: PropTypes.number,
  isSearchBoxEnabled: PropTypes.bool,
  isSearchBoxAutoTrigger: PropTypes.bool,
  customizedActionText: PropTypes.string,
  isActionAlwaysEnabled: PropTypes.bool,
  isActionAlwaysDisabled: PropTypes.bool,
  isPaginationEnabled: PropTypes.bool,
};

List.defaultProps = {
  defaultItemCount: 0,
  items: null,
  onItemSelect: () => {},
  onItemDeselect: () => {},
  isSelectedList: false,
  onSearch: () => {},
  onBulkOperation: () => {},
  customRenderBulkControl: null,
  customRenderItems: null,
  customRenderSelectedItems: null,
  customSearchPlaceHolder: '',
  isFiltered: false,
  isSearchBoxEnabled: true,
  isSearchBoxAutoTrigger: true,
  customizedActionText: null,
  isActionAlwaysEnabled: false,
  isActionAlwaysDisabled: false,
  isPaginationEnabled: true,
};

const ListWithStyles = withDefaultStyles(List, getStyles);
export { ListWithStyles as List };
